import { redirect, redirectDocument } from "react-router";
import { setTokenProvider } from "../utils/auth";
import { setAuthPortalRedirectUrl } from "../utils/api";
import {
  callAuthExchange,
  callPatchCurrentTenant,
  callWhoami,
} from "../utils/auth-api";
import { parseHashParams } from "../utils/url";
import { resolveLocalViteSignInUrl } from "../utils/localVite";
import type { WhoamiMe } from "../../shared-domain/auth-service/contract";

export interface AuthLoaderConfig {
  /** API エンドポイント */
  apiEndpoint: string;
  /** 認証ポータル URL */
  authPortalUrl: string;
  /** プロダクト ID */
  productId: string;
  /** トークンストア名（sessionStorage のキープレフィックス） */
  tokenStoreName: string;
  /** Cognito User Pool ID */
  userPoolId: string;
  /** Cognito Client ID */
  clientId: string;
  /** リフレッシュトークンAPIのURL */
  authRefreshUrl: string;
}

/**
 * 認証 loader を作成するヘルパー関数
 * @param config 認証設定
 * @returns loader 関数
 */
export function createAuthLoader(config: AuthLoaderConfig) {
  return async ({ request }: { request: Request }): Promise<WhoamiMe> => {
    const {
      apiEndpoint,
      authPortalUrl,
      productId,
      tokenStoreName,
      userPoolId,
      clientId,
      authRefreshUrl,
    } = config;

    // 認証ポータルの URL
    const params = new URLSearchParams();
    if (productId) {
      params.append("productId", productId);
    }

    // PRODUCT_DEV-421: 現在の URL の先頭セグメントがテナントID（スラッグ形式）であれば
    // 認証ポータルのパスにも `/{tenantId}/` を付与し、エイリアスログインモードを発動させる。
    // 例: unizo.umeee-app.com/umeee-3jz2/... → auth.umeee-app.com/umeee-3jz2/?productId=unizo
    // パターン: `prefix-suffix` 形式でサフィックスに数字を含む（既存ルート名 release-notes / meeting-groups
    // 等は数字を含まないため衝突しない）
    const requestUrl = new URL(request.url);
    const TENANT_ID_REGEX =
      /^([a-z0-9]+-[a-z0-9]*[0-9][a-z0-9]*)(\/|$)/i;
    const firstSegment = requestUrl.pathname.replace(/^\//, "");
    const tenantMatch = firstSegment.match(TENANT_ID_REGEX);
    const tenantPath = tenantMatch ? `/${tenantMatch[1]}/` : "/";
    const baseAuthUrl = authPortalUrl.replace(/\/+$/, "");
    const reAuthUrl = `${baseAuthUrl}${tenantPath}?${params.toString()}`;

    // PRODUCT_DEV-814: ローカル開発ノード(/vite/<product>/ 配下)では認証ポータルへ出さない。
    // 出すと Portal が productId で 1 件に絞って自動リダイレクトし、戻り先が DDB Products の
    // productUrl（= dev 環境の URL）になるため、ローカルのリンクを踏んだのに dev 環境が開く。
    // 同じノードの /vite/ 一覧（Portal ログイン導線あり）へ戻す。
    const localSignInUrl = resolveLocalViteSignInUrl(
      window.location.pathname,
      window.location.origin,
    );
    // ローカルでは `&reauth` / `&auto` の制御は意味を持たない（戻り先が静的ページのため）。
    const reauthTarget = (suffix = "") =>
      localSignInUrl ?? `${reAuthUrl}${suffix}`;

    /**
     * 未認証時のリダイレクトを投げる。
     *
     * ⚠ ローカルの戻り先は **同一オリジン** なので、素の `redirect()` だと react-router が
     * basename と突き合わせてクライアント遷移に潰すことがある
     * （`stripBasename(pathname, basename) != null` なら相対化される）。
     * dev-lab / admin は `basename="/vite/<product>"` なので `/vite/` は basename 外＝
     * ドキュメント遷移になるが、**unizo は createBrowserRouter に basename を渡しておらず**
     * （ルート route の `path` に BASE_URL を入れる方式）basename が "/" になるため、
     * `/vite/` へクライアント遷移し、どのルートにも一致せず SPA の 404 で行き止まりになる。
     * `redirectDocument` はその判定より先に効くので、router の設定に依存しなくなる。
     */
    // ⚠ 型注釈を変数側に置くこと。TS が「この呼び出しから先へは進まない」と解釈して
    // 後続を到達不能と扱うのは、明示的に `never` 型と注釈された識別子だけ。
    // アロー関数の戻り値注釈だけだと narrowing されず、`whoamiResponse.me` 等が
    // undefined 込みの型のまま残ってコンパイルが通らない。
    const throwReauth: (suffix?: string) => never = (suffix = "") => {
      if (localSignInUrl) throw redirectDocument(localSignInUrl);
      throw redirect(`${reAuthUrl}${suffix}`);
    };

    // PRODUCT_DEV-549: 401が強制リフレッシュ後も解消しない場合に shared-ui api.ts が
    // 遷移する認証ポータルURLを登録する（データAPIの永続401からの再ログイン導線）。
    // ここもローカルでは /vite/ 一覧にしないと、セッション途中の永続 401 から
    // 同じく dev 環境へ吸い込まれる。
    setAuthPortalRedirectUrl(reauthTarget());

    // Amplify の TokenProvider を初期化
    setTokenProvider(tokenStoreName, userPoolId, clientId, authRefreshUrl);

    // URL hash をパース
    const url = new URL(request.url);
    // const hash = parseHashParams(url.hash);
    const hashString = window.location.hash;
    const hash = parseHashParams(hashString);

    // authCode がある場合は hash を削除（履歴に残さない）
    if (hash.authCode) {
      const cleanUrl = `${url.pathname}${url.search}`;
      window.history.replaceState(null, "", cleanUrl);
    }

    try {
      // authCode がある場合はトークン交換
      let exchangeTenantId = null;
      if (hash.authCode) {
        const exchangeResponse = await callAuthExchange(
          hash.authCode,
          apiEndpoint,
          tokenStoreName,
        );

        if (!exchangeResponse.success) {
          console.error("Token exchange failed");
          throwReauth();
        }
        // ログイン画面で指定したテナントを初期表示する
        exchangeTenantId = exchangeResponse.body.tenantId;
      }

      // whoami で認証確認
      const whoamiResponse = await callWhoami(apiEndpoint);

      if (!whoamiResponse.success) {
        console.error("Authentication failed");
        // 自動ログインの無限リダイレクト防止
        // PRODUCT_DEV-549: `&logout`(Portal が global signOut=共有 refreshToken を revoke)ではなく
        // `&reauth`(Portal は revoke せず手動ログイン表示)に変更。auto 再試行が失敗した時のみ到達。
        if (hash.auto != null) {
          throwReauth("&reauth");
        }
        throwReauth();
      }

      // ログイン画面で指定したテナント (exchangeTenantId) がなければ
      // 最近表示したテナント (prevTenantId) を初期表示する
      const prevTenantId = whoamiResponse.me.currentTenantId;
      const tenantId = exchangeTenantId || prevTenantId;

      // テナントIDが取得できない場合は再ログインを促す
      // PRODUCT_DEV-549: revoke を誘発しない `&reauth` を使用（理由は上記コメント参照）。
      if (!tenantId) {
        throwReauth("&reauth");
      }

      // ログイン時（authCode交換あり）は常にlastLoginDateを更新する
      // テナント変更がない場合でもPATCHを呼ぶことでproductsInfo.lastLoginDateが更新される
      // ページリフレッシュ時（authCodeなし）はAPI呼び出しを省略する
      if (tenantId !== prevTenantId || exchangeTenantId) {
        await callPatchCurrentTenant(apiEndpoint, tenantId);
      }

      return {
        ...whoamiResponse.me,
        currentTenantId: tenantId,
      };
    } catch (error) {
      // redirect() による throw は再スロー
      if (error instanceof Response) {
        throw error;
      }

      // その他のエラーは認証ポータルへリダイレクト
      console.error("Auth loader error:", error);
      if (hash.auto != null) {
        throwReauth("&reauth");
      }
      throwReauth();
    }
  };
}
